// frontend/src/containers/Sidebar/Sidebar.js

import React, { useState } from 'react'
import { Link, NavLink as NavLinkRRD } from 'react-router-dom'
import PropTypes from 'prop-types'
import auth from '../../auth/service'
import FeedbackChoiceModal from '../../components/molecules/FeedbackChoiceModal'
import FeedbackFormModal from '../../components/molecules/FeedbackFormModal'
import {
  Button,
  Col,
  Collapse,
  Form,
  Input,
  InputGroup,
  Nav,
  Navbar,
  NavbarBrand,
  NavItem,
  NavLink,
  Row,
} from 'reactstrap'

const Sidebar = (props) => {
  const [collapseOpen, setCollapseOpen] = useState(false)
  // State to manage dropdowns. Key is the route name, value is boolean.
  const [dropdownsOpen, setDropdownsOpen] = useState({})
  const [showChoiceModal, setShowChoiceModal] = useState(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)

  const toggleDropdown = (name) => {
    // Create a unique key for the dropdown state
    const dropdownKey = name.replace(/[^a-zA-Z0-9]/g, '')
    setDropdownsOpen((prevState) => ({
      ...prevState,
      [dropdownKey]: !prevState[dropdownKey],
    }))
  }

  // closes the collapse
  const closeCollapse = () => setCollapseOpen(false)

  // creates the links that appear in the left menu / Sidebar
  const createLinks = (routes) => {
    return routes.map((prop, key) => {
      const hasAllowedService = (route) =>
        props.allowedServices &&
        props.allowedServices.some(
          (service) =>
            service.route === route.path ||
            service.route === (route.layout || '') + (route.path || '')
        )

      // Check if the route is a dropdown with children
      if (prop.childrens) {
        const isDropdownAllowed =
          !prop.private || hasAllowedService(prop) || prop.childrens.some(hasAllowedService)

        if (!isDropdownAllowed) {
          return null
        }

        const dropdownKey = prop.name.replace(/[^a-zA-Z0-9]/g, '')
        return (
          <div key={key}>
            <NavItem>
              <NavLink
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  toggleDropdown(dropdownKey)
                }}
                className={dropdownsOpen[dropdownKey] ? 'active' : ''}
              >
                <i className={prop.icon} />
                <span>{prop.name}</span>
                <i
                  className={`fas fa-chevron-down float-right arrow-icon ${
                    dropdownsOpen[dropdownKey] ? 'rotated' : ''
                  }`}
                />
              </NavLink>
              <Collapse isOpen={dropdownsOpen[dropdownKey]}>
                <Nav className="nav-sm flex-column ml-3">
                  {createLinks(prop.childrens)} {/* Recursive call for children */}
                </Nav>
              </Collapse>
            </NavItem>
            {prop.name === 'Segnalazione' && <hr className="my-3" />}
          </div>
        )
      }

      // Feedback action: handle before the general isAllowed check because the
      // '#segnalazione' path is not a real route in allowedServices and would
      // get filtered out for non-Webranking users.
      if (prop.isFeedbackAction) {
        const isWebranking = auth.userGroup === 'Webranking'
        return (
          <div key={key}>
            <NavItem>
              <NavLink
                href={isWebranking ? '#' : 'https://form.asana.com/?k=OKCsOEte-p6k1sUtDAl6Mg&d=280257083226273'}
                target={isWebranking ? undefined : '_blank'}
                onClick={(e) => {
                  if (isWebranking) {
                    e.preventDefault()
                    setShowChoiceModal(true)
                  }
                  closeCollapse()
                }}
              >
                <i className={prop.icon} />
                {prop.name}
              </NavLink>
            </NavItem>
            {prop.name === 'Segnalazione' && <hr className="my-3" />}
          </div>
        )
      }

      // Check if the single route is allowed
      const isAllowed =
        !prop.private ||
        (props.allowedServices &&
          props.allowedServices.some(
            (service) =>
              service.route === prop.path || service.route === (prop.layout || '') + prop.path
          ))

      if (!isAllowed) {
        return null
      }

      // Render a single link
      const finalPath = (prop.layout || '') + prop.path
      return (
        <div key={key}>
          <NavItem>
            <NavLink
              {...(prop.path.includes('http://') || prop.path.includes('https://')
                ? { href: prop.path, target: '_blank' }
                : { to: finalPath, tag: NavLinkRRD, exact: finalPath === '/' })}
              onClick={closeCollapse}
              activeClassName="active"
            >
              <i className={prop.icon} />
              {prop.name}
            </NavLink>
          </NavItem>
          {prop.name === 'Segnalazione' && <hr className="my-3" />}
        </div>
      )
    })
  }

  const { routes, logo, onLogout } = props
  let navbarBrandProps
  if (logo && logo.innerLink) {
    navbarBrandProps = { to: logo.innerLink, tag: Link }
  } else if (logo && logo.outterLink) {
    navbarBrandProps = { href: logo.outterLink, target: '_blank' }
  }

  return (
    <Navbar
      className="navbar-vertical fixed-left navbar-light bg-white"
      expand="md"
      id="sidenav-main"
    >
      <button
        className="navbar-toggler"
        type="button"
        onClick={() => setCollapseOpen(!collapseOpen)}
      >
        <span className="navbar-toggler-icon" />
      </button>
      {logo && (
        <NavbarBrand className="pt-4" {...navbarBrandProps}>
          <img
            width="80%"
            height="80%"
            alt={logo.imgAlt}
            className="navbar-brand-img"
            src={logo.imgSrc}
          />
        </NavbarBrand>
      )}
      <Collapse navbar isOpen={collapseOpen}>
        <div className="navbar-collapse-header d-md-none">
          <Row>
            {logo && (
              <Col className="collapse-brand" xs="6">
                {logo.innerLink ? (
                  <Link to={logo.innerLink}>
                    <img alt={logo.imgAlt} src={logo.imgSrc} />
                  </Link>
                ) : (
                  <a href={logo.outterLink}>
                    <img alt={logo.imgAlt} src={logo.imgSrc} />
                  </a>
                )}
              </Col>
            )}
            <Col className="collapse-close" xs="6">
              <button className="navbar-toggler" type="button" onClick={closeCollapse}>
                <span />
                <span />
              </button>
            </Col>
          </Row>
        </div>
        <Form className="mt-4 mb-3 d-md-none">
          <InputGroup className="input-group-rounded input-group-merge">
            <Input
              aria-label="Search"
              className="form-control-rounded form-control-prepended"
              placeholder="Search"
              type="search"
            />
          </InputGroup>
        </Form>
        {/* Render the links */}
        <Nav navbar>{createLinks(routes)}</Nav>

        {/* Logout button */}
        <Nav className="mb-md-3" navbar>
          <NavItem className="logout-button">
            <Button onClick={onLogout} outline color="info">
              Logout
            </Button>
          </NavItem>
        </Nav>
      </Collapse>
      <FeedbackChoiceModal
        show={showChoiceModal}
        onHide={() => setShowChoiceModal(false)}
        onOpenFeedbackForm={() => {
          setShowChoiceModal(false)
          setShowFeedbackModal(true)
        }}
      />
      <FeedbackFormModal
        show={showFeedbackModal}
        onHide={() => setShowFeedbackModal(false)}
        allowedServices={props.allowedServices}
      />
    </Navbar>
  )
}

Sidebar.defaultProps = {
  routes: [{}],
  allowedServices: [], // default empty array if not passed
}

Sidebar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
  allowedServices: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      route: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
  logo: PropTypes.shape({
    innerLink: PropTypes.string,
    outterLink: PropTypes.string,
    imgSrc: PropTypes.string.isRequired,
    imgAlt: PropTypes.string.isRequired,
  }),
  onLogout: PropTypes.func,
}

export default Sidebar
